import { useState, useEffect } from 'react'
import ResizableModal from '../../ui/ResizableModal'
import { Button, Title } from '../../ui/index'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DownloadIcon from '@mui/icons-material/Download'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)
dayjs.locale('es')

const STATUS_LABELS = {
  created: 'Creado',
  in_review: 'En revisión',
  solved: 'Resuelto'
}

const STATUS_COLORS = {
  created: 'bg-yellow-100 text-yellow-800',
  in_review: 'bg-blue-100 text-blue-800',
  solved: 'bg-green-100 text-green-800'
}

const TYPE_LABELS = {
  platform_error: 'Error de plataforma',
  payment_refund: 'Reembolso de pago'
}

export const TicketDetailModal = ({
  open,
  onClose,
  ticket,
  currentUserId,
  isAdmin,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  onSolveTicket,
  isAddingComment,
  isUpdatingComment,
  isSolvingTicket
}) => {
  const [commentMessage, setCommentMessage] = useState('')
  const [commentFiles, setCommentFiles] = useState([])
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editCommentMessage, setEditCommentMessage] = useState('')

  useEffect(() => {
    if (!open) {
      setCommentMessage('')
      setCommentFiles([])
      setEditingCommentId(null)
      setEditCommentMessage('')
    }
  }, [open])

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files)
    if (newFiles.length + commentFiles.length > 10) {
      return
    }
    setCommentFiles((prev) => [...prev, ...newFiles])
  }

  const removeFile = (index) => {
    setCommentFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id)
    setEditCommentMessage(comment.message)
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
    setEditCommentMessage('')
  }

  const handleSubmitComment = () => {
    if (!commentMessage.trim()) return

    if (commentFiles.length > 10) return

    onAddComment({
      message: commentMessage,
      files: commentFiles.length > 0 ? commentFiles : undefined
    })

    setCommentMessage('')
    setCommentFiles([])
  }

  const handleUpdateComment = (commentId) => {
    if (!editCommentMessage.trim()) return
    onUpdateComment(commentId, { message: editCommentMessage })
    setEditingCommentId(null)
    setEditCommentMessage('')
  }

  const handleDeleteComment = (commentId) => {
    if (!window.confirm('¿Estás seguro de eliminar este comentario?')) return
    onDeleteComment(commentId)
  }

  const handleSolveTicket = () => {
    if (!commentMessage.trim()) return

    if (commentFiles.length > 10) return

    onSolveTicket({
      message: commentMessage,
      files: commentFiles.length > 0 ? commentFiles : undefined
    })

    setCommentMessage('')
    setCommentFiles([])
  }

  const canEditComment = (comment) => {
    if (!ticket) return false
    if (comment.createdByUserId !== currentUserId) return false

    const commentIndex = ticket.comments.findIndex((c) => c.id === comment.id)
    if (commentIndex === -1) return false

    const nextComment = ticket.comments[commentIndex + 1]
    if (nextComment && nextComment.createdByUserId !== currentUserId) {
      return false
    }

    return true
  }

  const getFileName = (fileUrl) => {
    const splittedFileName = fileUrl.split('/').pop().split('-')

    if (splittedFileName.length > 5) {
      return splittedFileName.slice(5).join('-')
    }

    return splittedFileName.join('-')
  }

  if (!ticket) return null

  return (
    <ResizableModal
      open={open}
      onClose={onClose}
      width={900}
      height={800}
      minWidth={700}
      minHeight={600}
      footer={
        <>
          <Button
            onClick={onClose}
            className='uppercase text-sm bg-primary text-white rounded-full'
            title='CERRAR'
          />
          {ticket.status !== 'solved' && (
            <>
              {isAdmin && ticket.status === 'in_review' ? (
                <Button
                  onClick={handleSolveTicket}
                  disabled={isSolvingTicket || !commentMessage.trim()}
                  className='uppercase text-sm bg-green-600 text-white rounded-full'
                  title='RESOLVER TICKET'
                />
              ) : (
                <Button
                  onClick={handleSubmitComment}
                  disabled={isAddingComment || !commentMessage.trim()}
                  className='uppercase text-sm bg-accent2 text-white rounded-full'
                  title='ENVIAR'
                />
              )}
            </>
          )}
        </>
      }
    >
      <div className='relative w-full mx-auto'>
        <div className='flex items-center justify-between mb-4'>
          <Title className='text-2xl'>{ticket.title}</Title>
        </div>

        <div className='space-y-6'>
          <div className='flex gap-4'>
            <div>
              <span className='text-sm text-gray-500'>Estado:</span>
              <span
                className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status]}`}
              >
                {STATUS_LABELS[ticket.status]}
              </span>
            </div>
            <div>
              <span className='text-sm text-gray-500'>Tipo:</span>
              <span className='ml-2 text-sm'>{TYPE_LABELS[ticket.type]}</span>
            </div>
          </div>

          <div className='border-t pt-4'>
            <h3 className='font-semibold mb-4'>Comentarios</h3>
            <div className='space-y-4 max-h-96 overflow-y-auto'>
              {ticket.comments.map((comment) => (
                <div key={comment.id} className='border border-gray-300 rounded-lg p-4 bg-white'>
                  {editingCommentId === comment.id ? (
                    <div className='space-y-2'>
                      <textarea
                        value={editCommentMessage}
                        onChange={(e) => setEditCommentMessage(e.target.value)}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent2'
                        rows={3}
                      />
                      <div className='flex gap-2'>
                        <Button
                          onClick={() => handleUpdateComment(comment.id)}
                          disabled={isUpdatingComment}
                          className='uppercase text-xs bg-accent2 text-white rounded-full'
                          title='GUARDAR'
                        />
                        <Button
                          onClick={handleCancelEdit}
                          className='uppercase text-xs bg-primary text-white rounded-full'
                          title='CANCELAR'
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className='flex justify-between items-start mb-3'>
                        <p className='text-sm whitespace-pre-wrap text-gray-700 flex-1'>
                          {comment.message}
                        </p>
                        {canEditComment(comment) && (
                          <div className='flex gap-2 ml-4'>
                            <IconButton
                              size='small'
                              onClick={() => handleStartEditComment(comment)}
                              className='hover:bg-gray-100'
                            >
                              <EditIcon fontSize='small' />
                            </IconButton>
                            <IconButton
                              size='small'
                              onClick={() => handleDeleteComment(comment.id)}
                              className='hover:bg-gray-100'
                            >
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className='mt-3 pt-3 border-t border-gray-200'>
                          <p className='text-xs text-gray-500 mb-2 font-medium'>Archivos adjuntos:</p>
                          <div className='space-y-1'>
                            {comment.attachments.map((fileUrl, index) => (
                              <a
                                key={index}
                                href={fileUrl}
                                download
                                target='_blank'
                                rel='noopener noreferrer'
                                className='flex items-center gap-2 text-sm text-accent2 hover:text-accent1 transition-colors cursor-pointer p-2 rounded hover:bg-gray-50'
                              >
                                <AttachFileIcon fontSize='small' />
                                <span className='flex-1 truncate'>{getFileName(fileUrl)}</span>
                                <DownloadIcon fontSize='small' />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className='text-xs text-gray-400 mt-3'>
                        {dayjs(comment.createdAt).fromNow()}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {ticket.status !== 'solved' && (
            <div className='border-t pt-4'>
              <label className='block text-sm font-medium mb-2'>
                {isAdmin && ticket.status === 'in_review'
                  ? 'Resolver ticket (comentario obligatorio)'
                  : 'Agregar comentario'}
              </label>
              <textarea
                value={commentMessage}
                onChange={(e) => setCommentMessage(e.target.value)}
                className='w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent2 mb-2'
                rows={4}
                placeholder='Escribe tu mensaje...'
              />
              <div className='mb-2'>
                <input
                  type='file'
                  id='file-upload-detail'
                  multiple
                  onChange={handleFileChange}
                  className='hidden'
                />
                <label
                  htmlFor='file-upload-detail'
                  className='inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer bg-white hover:opacity-80'
                >
                  <AttachFileIcon fontSize='small' />
                  <span className='text-sm'>Adjuntar archivos (máx 10)</span>
                </label>
                {commentFiles.length > 0 && (
                  <div className='mt-2 space-y-1'>
                    {commentFiles.map((file, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between text-sm bg-white border border-gray-200 p-2 rounded'
                      >
                        <span className='text-gray-700'>{file.name}</span>
                        <IconButton
                          size='small'
                          onClick={() => removeFile(index)}
                          className='hover:bg-gray-100'
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ResizableModal>
  )
}
